<script lang="ts">
  import { Card, CustomButton, LoadingIndicator, Toast } from "$components";
  import { onDestroy, onMount } from "svelte";
  import { withApi } from "../lib/api-parkito";
  import { AuthMethod } from "../../apis/parkito/models/AuthMethod";
  import { default as QRCode } from "qrcode";
  import type { LoginState } from "../../apis/parkito/models/LoginState";
  import { session } from "../lib/session-parkito";

  const POLLING_INTERVAL = 1000;
  const BANKID_REDIRECT_QUERY_PARAM = "active-session";

  let loginSessionId: string | null = null;
  let intervalId: number | null = null;
  let qrCanvas: HTMLElement | null = null;
  let loginSessionType: AuthMethod | undefined = undefined;
  let bankidUrl: string | undefined = undefined;

  let startTokenMsg = "Öppnar BankID...";

  export let env: "mypages" | "dpo" = "dpo";

  const loginClicked = async (authMethod: AuthMethod) => {
    loginSessionType = authMethod;

    const loginRes = await withApi((api) => {
      return api.startLogin({
        ip: null,
        method: authMethod,
      });
    });

    if (loginRes.isOk()) {
      loginSessionId = loginRes.value.id;

      switch (authMethod) {
        case AuthMethod.QR:
          setQrCanvas(loginRes.value.qrCode);
          break;
        case AuthMethod.START_TOKEN:
          const bankIdUrl = buildBankIdUrl(
            loginRes.value.autoStartToken,
            loginSessionId,
          );

          console.log(`redirecting to ${bankIdUrl}`);

          window.location.href = bankIdUrl;
          break;
      }

      await startPolling();
    } else {
      if (import.meta.env.DEV) {
        console.log(`Login failed: ${loginRes.error}`);
      }
      Toast.error(`Ett fel uppstod vid inloggning. Försök igen senare.`);
    }
  };

  const buildBankIdUrl = (
    startToken: string,
    sessionId: string,
  ): string => {
    const url = new URL(window.location.href);

    // IOS requires the redirect URL to be set since the redirect
    // starts a new browser.
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      url.searchParams.set(BANKID_REDIRECT_QUERY_PARAM, sessionId);
      return `https://app.bankid.com/?autostarttoken=${startToken}&redirect=${url.toString()}`;
    } // On Android, BankID just returns to the previous app. So
    // no redirect URL is required.
    else {
      return `https://app.bankid.com/?autostarttoken=${startToken}`;
    }
  };

  const cancelClicked = async () => {
    clearInterval();
    if (loginSessionId) {
      await cancelRemoteLoginSession(loginSessionId);
      loginSessionId = null;
    }
  };

  const startPolling = async () => {
    await pollStatus(); // First poll directly.
    intervalId = window.setInterval(pollStatus, POLLING_INTERVAL);
  };

  const cancelRemoteLoginSession = async (sessionId: string) => {
    await withApi((api) => {
      return api.cancelLogin(loginSessionId!);
    });
  };

  const clearInterval = () => {
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  async function pollStatus() {
    console.log(`polling status`);
    if (loginSessionId) {
      let loginRes = await withApi((api) =>
        api.getLoginStatus(loginSessionId!)
      );

      console.log(`Login polled: ${loginRes}`);

      if (loginRes.isOk()) {
        const val = loginRes.value;
        let state: LoginState = val.state;

        if (state === "inProgress" && loginSessionType === AuthMethod.QR) {
          setQrCanvas(val.qrCode);
        } else if (state === "cancelled") {
          /// Cancelled by remote.
          if (import.meta.env.DEV) console.log("Login cancelled");

          clearInterval();
        } else if (typeof state === "object" && "failed" in state) {
          if (import.meta.env.DEV) {
            console.log("Login failed: ", state.failed.error);
          }
          clearInterval();
        } else if (typeof state === "object" && "succeeded" in state) {
          if (import.meta.env.DEV) console.log("Login successful");

          clearInterval();

          session.setState(state.succeeded.jwt, state.succeeded.user);
        }
      } else {
        console.log(`loginPoll error: ${loginRes.error}`);
      }
    }
  }

  const setQrCanvas = (qrData: string) => {
    QRCode.toCanvas(qrCanvas, qrData, {
      errorCorrectionLevel: "medium",
      scale: 4,
    });
  };

  onMount(() => {
    if (import.meta.env.DEV) console.log("Login mounted");
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const activeSession = searchParams.get(BANKID_REDIRECT_QUERY_PARAM);

    if (activeSession) {
      console.log("got an active session", activeSession);
      loginSessionId = activeSession;

      // Clear the read query param
      const newUrl = new URL(window.location.href);
      newUrl.search = ""; // Clear all query parameters
      window.history.replaceState({}, document.title, newUrl.toString());

      // Set to show info to user.
      loginSessionType = AuthMethod.START_TOKEN;
      startTokenMsg = "Väntar på svar från BankID";

      startPolling();
    } else if (token) {
      session.setState(token);
    } else {
      console.log(
        "got no token. Window location is:",
        window.location.href,
      );
      console.log("Window hash is:", window.location.hash);
    }
  });

  onDestroy(async () => {
    clearInterval();

    if (loginSessionId) {
      await cancelRemoteLoginSession(loginSessionId);
    }
  });
</script>

<div class="bg-[#0E4448] flex w-full min-h-screen items-center justify-center">
  {#if !loginSessionId}
    <div class="w-[370px] flex flex-col items-center text-center mb-[48px]">
      <img
        src={env === "dpo"
          ? "/graphics/illust-cars.svg"
          : "/graphics/illust-cars-2.svg"}
        alt="parkando-cars"
      />
      <img
        class="invert mt-[40px]"
        src="/graphics/logo.svg"
        alt="parkando-logo"
      />
      <p class="text-[white] mt-[24px] mb-[48px] text-lg">
        {#if env === "dpo"}
          Välkommen till Data Park Operatör. Vänligen logga in med BankID.
        {:else}
          Välkommen till Mina sidor. Vänligen logga in med BankID.
        {/if}
      </p>

      <CustomButton
        type="white"
        customClass="min-w-[280px] mb-[16px]"
        onClick={() => loginClicked(AuthMethod.START_TOKEN)}
      ><img
          src="/icons/bankid.svg"
          class="mr-[8px]"
          alt="external-link"
        />BankID på denna enhet</CustomButton>

      <CustomButton
        type="white"
        customClass="min-w-[280px]"
        onClick={() => loginClicked(AuthMethod.QR)}
      ><img
          src="/icons/bankid.svg"
          class="mr-[8px]"
          alt="external-link"
        />BankID med QR-kod</CustomButton>
    </div>
  {:else if loginSessionType === AuthMethod.QR}
    <Card>
      <div class="w-[370px] flex flex-col items-center text-center">
        <h2 class="mt-[24px]">Logga in med Mobilt BankID</h2>
        <p class="mt-[24px] mb-[42px] text-[16px]">
          Starta Bank ID-appen och läs av QR-koden.
        </p>
        <canvas bind:this={qrCanvas}></canvas>
        <button
          class="text-lg font-semibold text-[#181818] block pt-[8px] pb-[16px] text-[16px] mt-[42px]"
          on:click={cancelClicked}
        >
          Avbryt
        </button>
      </div>
    </Card>
  {:else if loginSessionType === AuthMethod.START_TOKEN}
    <Card>
      <div class="w-[370px] flex flex-col items-center text-center">
        <h2 class="mt-[24px]">Logga in med Mobilt BankID</h2>
        <p class="mt-[24px] mb-[8px] text-[16px]">{startTokenMsg}</p>
        <LoadingIndicator visible={true} customClass="py-[48px]" />
        <p class="mt-[16px] mb-[24px] text-[14px] px-[16px]">
          <a href={bankidUrl} class="underline"
          >Om BankID-appen inte startar automatiskt, klicka här för att öppna
            den manuellt.</a>
        </p>
        <button
          class="text-lg font-semibold text-[#181818] block pt-[8px] pb-[16px] text-[16px] mt-[32px]"
          on:click={cancelClicked}
        >
          Avbryt
        </button>
      </div>
    </Card>
  {/if}

  <div class="absolute bottom-0 p-[16px]">
    <p class="text-[#9FB1B2]">© Parkando AB {new Date().getFullYear()}</p>
  </div>
</div>
